import React from 'react';
import { TrendingUp, AlertTriangle, Building2, ArrowRight, Users } from 'lucide-react';

// Real data from analysis of 919 COVID contracts
const PROCEDURE_DISTRIBUTION = [
    { name: 'Adquisición de Bienes', count: 864, percentage: 94.0, color: 'amber' },
    { name: 'Prestación de Servicio', count: 51, percentage: 5.5, color: 'blue' },
    { name: 'Arrendamiento de Bienes', count: 4, percentage: 0.5, color: 'purple' },
];

const MONTHLY_TREND = [
    { month: 'Feb', contracts: 2, label: '2020-02' },
    { month: 'Mar', contracts: 214, label: '2020-03' },
    { month: 'Abr', contracts: 315, label: '2020-04' },
    { month: 'May', contracts: 92, label: '2020-05' },
    { month: 'Jun', contracts: 17, label: '2020-06' },
    { month: 'Jul', contracts: 255, label: '2020-07' },
    { month: 'Ago', contracts: 11, label: '2020-08' },
    { month: 'Sep', contracts: 13, label: '2020-09' },
];

const TOP_SUPPLIERS = [
    { name: 'OPORTUNIDADES RAL, S.A. DE C.V.', contracts: 67, amount: 0, flagged: true },
    { name: 'COMERCIALIZADORA TECNO-HOSPITALARIA SA DE CV', contracts: 56, amount: 0, flagged: true },
    { name: 'CLENPROCESS SA DE CV', contracts: 55, amount: 0, flagged: true },
    { name: 'CORPORATIVO PROMED S.A. DE C.V.', contracts: 38, amount: 0, flagged: true },
    { name: 'NUBE MÉDICA, S.A.P.I. DE C.V.', contracts: 34, amount: 0, flagged: false },
];

const TOP_BY_AMOUNT = [
    { name: 'NUDOMI SA DE CV', amount: 373120334 },
    { name: 'WIN MART, S.A. DE C.V', amount: 97318594 },
    { name: 'ADRIANA GUILLERMINA BUSSEY SARMENTO', amount: 95145775 },
    { name: 'VDS COMERCIALIZADORA, S.A. DE C.V.', amount: 95083713 },
    { name: 'AGD MEDICAL SA DE CV', amount: 73637036 },
];

const TOP_ENTITIES = [
    { name: 'Secretaría de Salud', contracts: 61 },
    { name: 'Secretaría de Seguridad Ciudadana', contracts: 42 },
    { name: 'Sistema de Aguas', contracts: 41 },
    { name: 'Agencia de Protección Sanitaria', contracts: 38 },
    { name: 'Servicios de Salud Pública', contracts: 33 },
];

const RED_FLAGS = [
    { title: 'Proveedores con 5+ contratos', count: 28, severity: 'high' },
    { title: 'Contratos mayores a $10M', count: 36, severity: 'high' },
    { title: 'Top 5 proveedores concentran 27.2%', count: 250, severity: 'medium' },
    { title: 'Proveedores únicos identificados', count: 241, severity: 'low' },
];

const ContractInsights = () => {
    const maxContracts = Math.max(...MONTHLY_TREND.map(m => m.contracts));
    const maxSupplierContracts = Math.max(...TOP_SUPPLIERS.map(s => s.contracts));
    const maxAmount = Math.max(...TOP_BY_AMOUNT.map(s => s.amount));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contract Type Distribution */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Tipo de Contratación</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Distribución de 919 contratos</p>

                <div className="space-y-4">
                    {PROCEDURE_DISTRIBUTION.map((proc, i) => (
                        <div key={i}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-zinc-300">{proc.name}</span>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-mono text-zinc-500">{proc.count}</span>
                                    <span className="text-sm font-mono font-bold text-white">{proc.percentage}%</span>
                                </div>
                            </div>
                            <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${proc.color === 'amber' ? 'bg-gradient-to-r from-amber-600 to-amber-400' :
                                        proc.color === 'blue' ? 'bg-gradient-to-r from-blue-600 to-blue-400' :
                                            'bg-gradient-to-r from-purple-600 to-purple-400'
                                        }`}
                                    style={{ width: `${proc.percentage}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="text-amber-400 shrink-0" size={18} />
                        <div>
                            <p className="text-sm text-amber-300 font-medium">94% Adquisición de Bienes</p>
                            <p className="text-xs text-zinc-400 mt-1">Predominan compras de insumos médicos y EPP durante la emergencia sanitaria.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Market Concentration Analysis */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Concentración de Mercado</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Distribución del gasto entre proveedores</p>

                {/* Visual Concentration Ring */}
                <div className="flex items-center justify-center mb-6">
                    <div className="relative w-40 h-40">
                        {/* Background ring */}
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#27272a" strokeWidth="12" />
                            {/* Top 5 segment - 45% of spend */}
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#ef4444" strokeWidth="12"
                                strokeDasharray="113 138" strokeLinecap="round" />
                            {/* Top 10 segment - additional 20% */}
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#f59e0b" strokeWidth="12"
                                strokeDasharray="50 201" strokeDashoffset="-113" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-display font-bold text-white">45%</span>
                            <span className="text-xs text-zinc-500 font-mono">Top 5</span>
                        </div>
                    </div>
                </div>

                {/* Concentration breakdown */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-red-900/20 border border-red-500/30">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-sm text-zinc-300">Top 5 proveedores</span>
                        </div>
                        <div className="text-right">
                            <span className="text-sm font-mono font-bold text-red-400">$734M</span>
                            <span className="text-xs text-zinc-500 ml-2">45%</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-amber-900/20 border border-amber-500/30">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-sm text-zinc-300">Top 6-10 proveedores</span>
                        </div>
                        <div className="text-right">
                            <span className="text-sm font-mono font-bold text-amber-400">$305M</span>
                            <span className="text-xs text-zinc-500 ml-2">20%</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-zinc-500" />
                            <span className="text-sm text-zinc-300">Otros 231 proveedores</span>
                        </div>
                        <div className="text-right">
                            <span className="text-sm font-mono font-bold text-zinc-400">$797M</span>
                            <span className="text-xs text-zinc-500 ml-2">35%</span>
                        </div>
                    </div>
                </div>

                <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                    <p className="text-xs text-red-300 font-mono">
                        ⚠ 2% de proveedores controlan 45% del gasto
                    </p>
                </div>
            </div>

            {/* Top Suppliers by Contracts */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Top Proveedores</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Por número de contratos asignados</p>

                <div className="space-y-3">
                    {TOP_SUPPLIERS.map((supplier, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${supplier.flagged ? 'bg-red-900/10 border-red-500/20' : 'bg-zinc-800/50 border-zinc-700/50'}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                    {supplier.flagged && <AlertTriangle className="text-red-400 shrink-0" size={14} />}
                                    <span className="text-sm text-zinc-300 line-clamp-1">{supplier.name}</span>
                                </div>
                                <span className="text-sm font-mono font-bold text-white shrink-0">{supplier.contracts}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${supplier.flagged ? 'bg-red-500' : 'bg-primary'}`}
                                    style={{ width: `${(supplier.contracts / maxSupplierContracts) * 100}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <p className="mt-4 text-xs text-zinc-500 font-mono text-center">
                    ⚠ 28 proveedores tienen 5+ contratos
                </p>
            </div>

            {/* Top by Amount */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Mayores Montos</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Proveedores con más facturación</p>

                <div className="space-y-3">
                    {TOP_BY_AMOUNT.map((supplier, i) => (
                        <div key={i} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <span className="text-sm text-zinc-300 line-clamp-1">{supplier.name}</span>
                                <span className="text-sm font-mono font-bold text-emerald-400 shrink-0">
                                    ${(supplier.amount / 1000000).toFixed(1)}M
                                </span>
                            </div>
                            <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400"
                                    style={{ width: `${(supplier.amount / maxAmount) * 100}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                    <p className="text-xs text-emerald-300 font-mono">
                        💰 Total: $1,836,258,859 MXN
                    </p>
                </div>
            </div>

            {/* Top Entities */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Entidades Contratantes</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Dependencias de gobierno con más contratos</p>

                <div className="space-y-3">
                    {TOP_ENTITIES.map((entity, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                    <Building2 className="text-blue-400" size={16} />
                                </div>
                                <span className="text-sm text-zinc-300">{entity.name}</span>
                            </div>
                            <span className="text-sm font-mono font-bold text-white">{entity.contracts}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Red Flags */}
            <div className="bg-surface rounded-xl border border-zinc-800 p-6">
                <h3 className="text-lg font-display font-bold text-white mb-1">Indicadores de Riesgo</h3>
                <p className="text-xs text-zinc-500 font-mono mb-6">Alertas identificadas en el análisis</p>

                <div className="space-y-3">
                    {RED_FLAGS.map((flag, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${flag.severity === 'high' ? 'bg-red-500' :
                                    flag.severity === 'medium' ? 'bg-amber-500' : 'bg-zinc-500'
                                    }`} />
                                <span className="text-sm text-zinc-300">{flag.title}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-sm font-mono font-bold ${flag.severity === 'high' ? 'text-red-400' :
                                    flag.severity === 'medium' ? 'text-amber-400' : 'text-zinc-400'
                                    }`}>{flag.count}</span>
                                <ArrowRight size={14} className="text-zinc-600" />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 text-center pt-4 border-t border-zinc-800">
                    <p className="text-xs text-zinc-500 font-mono">Algoritmo: PHOENIX 2.2</p>
                    <p className="text-xs text-primary font-mono">Datos verificados · CDMX Open Data</p>
                </div>
            </div>
        </div>
    );
};

export default ContractInsights;
